import path from 'path';
import * as pluginInfo from '../package.json';
import { transformJsx } from './jsx';
import { transformVue } from './vue';
import type { PluginOptions, TTransformer } from './type';
import crypto from 'node:crypto';
import { isIncludedFile } from './utils';

const transformerFileTypes: Record<string, string[]> = {
  vue: ['vue'],
  jsx: ['jsx', 'tsx', 'js', 'ts'],
};

const transformers: Record<string, TTransformer> = {
  vue: transformVue,
  jsx: transformJsx,
};

const PREFIX = 'locator';
const DEFAULT_INCLUDE = ['src/**/*.{vue,jsx,tsx,js,ts}'];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const cache = new Map<string, { code: string; map?: any }>();

function getHash(code: string) {
  return crypto.createHash('md5').update(code).digest('hex');
}

export function viteSourceLocator(options: PluginOptions = {}) {
  const { include = DEFAULT_INCLUDE, exclude = [], prefix = PREFIX, enable = true, attributes, filterTag } = options;

  return {
    name: pluginInfo.name,
    enforce: 'pre' as const,
    apply: 'serve' as const,
    version: pluginInfo.version,
    async transform(code: string, id: string) {
      if (!enable) return;
      const relativePath = path.relative(process.cwd(), id);

      if (!isIncludedFile(relativePath, include, exclude)) return;

      const hash = getHash(code);
      const cacheKey = `${id}:${hash}`;
      if (cache.has(cacheKey)) {
        const value = cache.get(cacheKey);
        return value;
      }

      const transformer = Object.keys(transformers).find((key) => {
        const fileTypes = transformerFileTypes[key];
        return fileTypes.some((type) => id.endsWith(`.${type}`));
      });

      if (!transformer) {
        return;
      }

      const result = transformers[transformer]?.(code, id, prefix, { attributes, filterTag });

      cache.set(cacheKey, result);
      return result;
    },
  };
}
